import moment from 'moment';
import { cloneDeep, head, map } from 'lodash';

class QuickBookController {

    constructor($window, basket, eventService, availabilityApiService, loginoutService, quantityService, configService, dateService, validateService, messageService, availabilityService) {
        this.$window = $window;
        this.basket = basket;
        this.eventService = eventService;
        this.availabilityApiService = availabilityApiService;
        this.loginoutService = loginoutService;
        this.quantityService = quantityService;
        this.configService = configService;
        this.dateService = dateService;
        this.validateService = validateService;
        this.messageService = messageService;
        this.availabilityService = availabilityService;
    }

    $onInit () {
        this.loading = ['events'];

        this.showTreatments = true;
        this.showSpaDays = true;

        this.hasTreatments = false;
        this.hasSpaDays = false;

        this.fromDate = this.dateService.checkStoredDate();
        this.toDate = moment(this.fromDate).add(6, 'days').format('YYYY-MM-DD');

        return this.basket.promise && this.basket.promise.then(() => {
            this.guest = head(this.basket.contents.guest_details);
            return this.getAllEvents();
        });
    }

    getAllEvents () {
        return this.eventService.getAllEvents()
            .then(response => {
                this.stopLoading('events');
                this.originalData = response;
                this.filterEvents();
            })
            .catch(() => { });
    }

    selectEvent (item) {
        this.resetAvailability(false);

        this.loading.push('availability');
        this.item = item;
        this.item.preferred_time = undefined;
        this.item.date = this.fromDate;

        return this.availabilityApiService.requestAvailability(this.fromDate, this.toDate, item.type_name, item.id).then(response => {
            this.stopLoading('availability');
            this.availability = response;

            if (this.$window.fpInput) {
                this.$window.fpInput.setDate(head(this.availability).date, false, 'Y-m-d');
            }
            return;
        });
    }

    placeholder () {
        if (this.isLoading('events')) {
            return 'Loading...';
        }

        let string = 'Search';

        if (this.hasTreatments) {
            string = `${string} Treatments`;
        }

        if (this.hasTreatments && this.hasSpaDays) {
            string = `${string} and`;
        }

        if (this.hasSpaDays) {
            string = `${string} Spa Days`;
        }

        return `${string}...`;
    }

    groupEvents (item) {
        return item.type_display;
    }

    displayUrl () {
        return `/${this.slugify(this.item.type_display)}/${this.item.slug}`;
    }

    slugify (string) {
        return string.replace(/\s/g, '-').toLowerCase();
    }

    stopLoading (param) {
        const index = this.loading.indexOf(param);
        if (index > -1) {
            this.loading.splice(index, 1);
        }
    }

    isLoading (param) {
        if (param) {
            return this.loading.indexOf(param) > -1;
        }

        return this.loading.length > 0;
    }

    buttonDisabled () {
        return this.isLoading() || !this.availabilityService.hasSingleTimes();
    }

    filter (param) {
        switch (param) {
            case 'treatments':
                this.showTreatments = !this.showTreatments;
                break;
            case 'spadays':
                this.showSpaDays = !this.showSpaDays;
                break;
        }

        this.filterEvents();
    }

    filterEvents () {
        this.resetAvailability();

        if (!this.originalData) {
            return;
        }

        this.data = cloneDeep(this.originalData);
        this.data.events = this.data.events.filter(event => {
            if (this.guest.gender
                && event.gender
                && event.gender !== this.guest.gender) {
                return false;
            }

            if (event.type_name === 'Treatment'
                && !this.showTreatments) {
                return false;
            }

            if (event.type_name === 'SpaDay'
                && !this.showSpaDays) {
                return false;
            }

            if (this.filterCategory
                && event.categories.indexOf(parseInt(this.filterCategory)) === -1) {
                return false;
            }

            if (this.filterDuration
                && (event.display_duration && '' + event.display_duration !== '' + this.filterDuration
                    || !event.display_duration && '' + event.duration !== '' + this.filterDuration)) {
                return false;
            }

            switch (event.type_name) {
                case 'Treatment':
                    this.hasTreatments = true;
                    break;
                case 'SpaDay':
                    this.hasSpaDays = true;
                    break;
            }

            return true;
        });

        if (!this.data.events.length) {
            this.noEvents = true;
        }
    }

    resetAvailability (resetEvent = true) {
        this.item = undefined;
        this.availability = undefined;
        this.noEvents = false;
        this.staff = undefined;

        this.availabilityService.resetSingleDays();
        this.availabilityService.resetSingleTimes();

        if (resetEvent) {
            this.selectedEvent = undefined;
        }
    }

    resetFilters () {
        this.showTreatments = true;
        this.showSpaDays = true;
        this.filterCategory = '';
        this.filterDuration = '';
        this.noEvents = false;

        this.filterEvents();
    }

    isLoggedIn () {
        if (!this.loginoutService.isLoggedIn()) {
            if (this.item) {
                this.resetAvailability();
                this.resetFilters();
            }
            return false;
        }

        if (!this.guest && this.basket.contents.guest_details.length) {
            this.guest = head(this.basket.contents.guest_details);
            this.resetFilters();
            this.$window.dispatchEvent(new CustomEvent('initCalendar'));
        }

        return true;
    }

    getQuantity () {
        this.quantity = undefined;

        if (this.item.preferred_time) {
            this.loading.push('quantity');
            this.resetStaff();

            return this.quantityService.getQuantity(this.item).then(response => {
                this.staff = map(response.staff, (staffId, staffName) => {
                    return {
                        id: staffId,
                        label: staffName
                    };
                }) || undefined;

                this.stopLoading('quantity');
            });
        }
    }

    resetStaff () {
        delete this.item.staff;

        if (this.staff) {
            for (let staff of this.staff) {
                delete (staff.disabled);
            }
        }
    }

    canDisplayStaff () {
        return !!this.configService.get('property').loadStaff;
    }

    displayStaff () {
        return !!(this.canDisplayStaff()
            && !this.isLoading()
            && this.staff
            && this.staff.length);
    }

    addToBasket () {
        this.item.quantity = 1;

        let error = this.validateService.invalidItemOrGuests(this.item, [this.guest]);
        if (error) {
            this.messageService.add(error);
            this.messageService.failure();
            return;
        }

        this.loading.push('addToBasket');

        this.basket.changeStatus('checkout');

        return this.basket.addToBasket([this.guest], this.item, false)
            .then(() => {
                if (!this.basket.success) {
                    this.stopLoading('addToBasket');
                    this.selectEvent(this.item);
                    return;
                }

                this.$window.location = `/checkout/${this.basket.contents.first_step}`;
            });
    }

    login () {
        return this.loginoutService.login();
    }
}

QuickBookController.$inject = ['$window', 'basket', 'eventService', 'availabilityApiService', 'loginoutService', 'quantityService', 'configService', 'dateService', 'validateService', 'messageService', 'availabilityService'];

export default QuickBookController;



// WEBPACK FOOTER //
// ./frontend/storefront/app/modules/page/controllers/quickBook.controller.js